import { AUTH0_NON_PROD_DOMAIN, AUTH0_NON_PROD_CLIENT_ID } from './auth0-constants';
import { VERCEL_PREVIEW_ORIGIN_RE } from './origins';
import { reactPlugin } from '@datadog/browser-rum-react';
import { keepUnlessConsoleCopyOfBoundaryError } from 'helpers/boundaryCaughtErrors';

const PROD_ORIGIN = 'https://app.oath.vote';
const STAGING_ORIGIN = 'https://test.oath.vote';
// Anchored to the full hostname so look-alikes (e.g. oath.vote.local.copycat.com) don't match.
const LOCAL_HOSTNAME_RE = /^(oath\.vote\.local|localhost|127\.0\.0\.1)$/;

// Same-origin proxy to Datadog (see api/dd-logs.ts). Ad-block lists block Datadog by domain, so routing through our own origin circumvents ad-blocking.
const DD_PROXY_URI = '/api/dd-logs';

const RELEASE_VERSION = process.env.REACT_APP_VERCEL_GIT_COMMIT_SHA;

const DATADOG_LOGS_INIT_BASE = {
  clientToken: 'pub656a000b47d109fa06242c68cd195799',
  site: 'datadoghq.com',
  forwardErrorsToLogs: false,
  service: 'browser-oath-site',
  version: RELEASE_VERSION,
  sessionSampleRate: 100,
  proxy: DD_PROXY_URI,
};

const DATADOG_RUM_INIT_BASE = {
  applicationId: 'b0a8ed81-edd2-4739-bbd9-e300545b8467',
  clientToken: 'pub4b3755ad2f0d965f3e732a9140210854',
  remoteConfiguration: {
    id: 'aec6875f-7fcc-4e82-8fb6-7709a79057dd',
  },
  site: 'datadoghq.com',
  proxy: DD_PROXY_URI,
  service: 'rum-oath-site',
  version: RELEASE_VERSION,
  sessionSampleRate: 100,
  sessionReplaySampleRate: 100,
  trackAnonymousUser: true,
  trackResources: true,
  trackUserInteractions: true,
  trackLongTasks: true,
  plugins: [reactPlugin({ router: false })],
  beforeSend: keepUnlessConsoleCopyOfBoundaryError,
};

const MODE_OFF = 'off';

const CLOUDFLARE_ORIGIN_AUTH_MODES = [MODE_OFF, 'log', 'enforce'] as const;

type CloudflareOriginAuthMode = (typeof CLOUDFLARE_ORIGIN_AUTH_MODES)[number];

/** Anything unrecognised is treated as off, so a bad value can never strand the app on a host it cannot reach. */
const toCloudflareOriginAuthMode = (value: unknown): CloudflareOriginAuthMode =>
  CLOUDFLARE_ORIGIN_AUTH_MODES.includes(value as CloudflareOriginAuthMode)
    ? (value as CloudflareOriginAuthMode)
    : MODE_OFF;

const IS_API_ROUTED_THROUGH_CLOUDFLARE =
  toCloudflareOriginAuthMode(process.env.REACT_APP_CLOUDFLARE_ORIGIN_AUTH_MODE) !== MODE_OFF;

/**
 * Marks an environment that has no Cloudflare-fronted API host, so the build-mode flag cannot
 * route it through Cloudflare no matter what value it ships with: only prod and staging have a
 * proxied hostname provisioned.
 */
const NO_CLOUDFLARE_FRONTED_HOST = null;

const PROD_API_BASE_URL = 'https://api-prod-0fto.onrender.com';
const PROD_CLOUDFLARE_API_BASE_URL = 'https://api-prod.oathvote.app';

const PROD_CONFIG = {
  MODE: 'PRODUCTION',
  // harcoding for production because it never changes and it's more secure
  SITE_URL: PROD_ORIGIN,
  ENGAGE_URL: 'https://contribute.oath.vote/checkout',
  DONATE_URL: 'https://donate.oath.vote/page/',
  CLOUD_STORAGE: {
    URL: 'https://storage.googleapis.com/oath_1',
    TAGS: 'tags',
    SETS: 'sets',
  },
  AUTH: {
    DOMAIN: 'login-oath-vote.us.auth0.com',
    CLIENT_ID: 'OCSh8SNYKiOjoWubWoZ5FR7khnaM87k5',
  },
  DEMOCRACY_ENGINE: {
    CHECKOUT_URL: 'https://donate.oath.vote/page/secure/checkout',
    DONATE_API_BASE_URL: 'https://donate.democracyengine.com',
    OATH_TIP_RECIPIENT_ID: 'a65076e2-2bdb-49e4-8462-120159d62ec6',
  },
  API: {
    BASE_URL: PROD_API_BASE_URL,
    CLOUDFLARE_BASE_URL: PROD_CLOUDFLARE_API_BASE_URL,
  },
  DATADOG_LOGS_INIT: {
    ...DATADOG_LOGS_INIT_BASE,
    env: 'prod',
  },
  DATADOG_RUM_INIT: {
    ...DATADOG_RUM_INIT_BASE,
    env: 'prod',
    allowedTracingUrls: [PROD_API_BASE_URL, PROD_CLOUDFLARE_API_BASE_URL],
  },
};

// Locked staging backend — STAGING_CONFIG always hits this regardless of any other defined env config.
const STAGING_API_BASE_URL = 'https://api-staging.oathvote.app';
const STAGING_CLOUDFLARE_API_BASE_URL = 'https://api-staging.oathvote.app';

// Local dev hits /backend (same origin); Caddy proxies it to BACKEND_UPSTREAM.
const LOCAL_API_BASE_URL =
  process.env.NODE_ENV === 'development' && process.env.REACT_APP_ENV === 'local'
    ? '/backend'
    : STAGING_API_BASE_URL;

// Warn (instead of failing silently) when we're on a local host but the /backend
// proxy is off — requests would go straight to staging, bypassing Caddy.
// Skipped under test (jsdom runs on localhost, which would trip this on every suite).
if (
  process.env.NODE_ENV !== 'test' &&
  LOCAL_HOSTNAME_RE.test(window.location.hostname) &&
  LOCAL_API_BASE_URL !== '/backend'
) {
  console.warn(
    `[env] Local host but REACT_APP_ENV="${process.env.REACT_APP_ENV}" (expected "local"): ` +
      `bypassing the Caddy /backend proxy and calling ${STAGING_API_BASE_URL} directly. ` +
      `Start via "make up" with .env.local present.`
  );
}

const NON_PROD_BASE = {
  // Use window.location.origin to support dynamic Vercel deployment URLs
  SITE_URL: window.location.origin,
  ENGAGE_URL: 'https://test.engageraise.com/checkout',
  DONATE_URL: 'https://donate.oath.vote/page/',
  CLOUD_STORAGE: {
    URL: 'https://storage.googleapis.com/oath_1',
    TAGS: 'test_tags',
    SETS: 'test_sets',
  },
  AUTH: {
    DOMAIN: AUTH0_NON_PROD_DOMAIN,
    CLIENT_ID: AUTH0_NON_PROD_CLIENT_ID,
  },
  DEMOCRACY_ENGINE: {
    CHECKOUT_URL: 'https://donate.oath.vote/page/secure/checkout',
    DONATE_API_BASE_URL: 'https://clientstage-donate.democracyengine.com',
    OATH_TIP_RECIPIENT_ID: 'Oath API927510000',
  },
};

const STAGING_CONFIG = {
  ...NON_PROD_BASE,
  MODE: 'STAGING',
  API: { BASE_URL: STAGING_API_BASE_URL, CLOUDFLARE_BASE_URL: STAGING_CLOUDFLARE_API_BASE_URL },
  DATADOG_LOGS_INIT: { ...DATADOG_LOGS_INIT_BASE, env: 'staging' },
  DATADOG_RUM_INIT: {
    ...DATADOG_RUM_INIT_BASE,
    env: 'staging',
    allowedTracingUrls: [STAGING_API_BASE_URL],
  },
};

// test comment
const PREVIEW_CONFIG = {
  ...NON_PROD_BASE,
  MODE: 'PREVIEW',
  API: {
    BASE_URL: STAGING_API_BASE_URL,
    CLOUDFLARE_BASE_URL: NO_CLOUDFLARE_FRONTED_HOST,
  },
  DATADOG_LOGS_INIT: { ...DATADOG_LOGS_INIT_BASE, env: 'preview' },
  DATADOG_RUM_INIT: null,
};

const LOCAL_CONFIG = {
  ...NON_PROD_BASE,
  MODE: 'LOCALHOST',
  API: { BASE_URL: LOCAL_API_BASE_URL, CLOUDFLARE_BASE_URL: NO_CLOUDFLARE_FRONTED_HOST },
  DATADOG_LOGS_INIT: { ...DATADOG_LOGS_INIT_BASE, env: 'local' },
  DATADOG_RUM_INIT: null,
};

const selectConfig = (origin: string, hostname: string) => {
  if (origin === PROD_ORIGIN) return PROD_CONFIG;
  if (origin === STAGING_ORIGIN) return STAGING_CONFIG;
  if (VERCEL_PREVIEW_ORIGIN_RE.test(origin)) return PREVIEW_CONFIG;
  if (LOCAL_HOSTNAME_RE.test(hostname)) return LOCAL_CONFIG;
  // Unknown origins fall back to staging
  return STAGING_CONFIG;
};

// Matches a relative path or a local hostname anywhere in an API base URL.
const LOCAL_API_URL_RE = /^\/|localhost|127\.0\.0\.1|oath\.vote\.local/;

type ResolvedEnvConfig = Omit<ReturnType<typeof selectConfig>, 'API'> & {
  API: { BASE_URL: string };
};

export const ENV_CONFIG = (): ResolvedEnvConfig => {
  const { origin, hostname } = window.location;
  const config = selectConfig(origin, hostname);
  const cloudflareBaseUrl = IS_API_ROUTED_THROUGH_CLOUDFLARE
    ? config.API.CLOUDFLARE_BASE_URL
    : NO_CLOUDFLARE_FRONTED_HOST;
  const baseUrl = cloudflareBaseUrl ?? config.API.BASE_URL;

  // Defense-in-depth: a relative or local API URL must never be used off a local host.
  // Catches a future regression that points a cloud config at /backend or localhost,
  // forcing a safe staging fallback instead of a broken (or data-leaking) request.
  const isLocalHost = LOCAL_HOSTNAME_RE.test(hostname);
  if (!isLocalHost && LOCAL_API_URL_RE.test(baseUrl)) {
    console.error(
      `[env] Blocked local API URL "${baseUrl}" on ${origin}; falling back to staging.`
    );
    return { ...config, API: { BASE_URL: STAGING_API_BASE_URL } };
  }
  return { ...config, API: { BASE_URL: baseUrl } };
};
